import { styled } from '@mui/material/styles';
import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { semantic } from '../../../theme/tokens/semantic';

/**
 * @tier atom
 *
 * Color key for the icon glyph. Resolves to `theme.palette[color].main`.
 * `neutral` and `purple` are extended palette keys (see `palette.ts`).
 */
export type VyIconTileColor =
  | 'primary'
  | 'secondary'
  | 'success'
  | 'warning'
  | 'info'
  | 'error'
  | 'neutral'
  | 'purple';

/**
 * 36×36 tinted tile that holds an icon. The tile background is the design
 * system's `BG/Subtle` (a soft gray). The `color` prop sets the icon's
 * color via CSS `color`, which the icon picks up via `currentColor`.
 *
 * The icon child should be a Vy icon component (or any SVG using
 * `currentColor`). It will inherit the tile's color and override any
 * baked-in default the icon may carry.
 */
export interface VyIconTileProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * Icon glyph color. Maps to `theme.palette[color].main`.
   * Default: `'neutral'`.
   */
  color?: VyIconTileColor;
  /** The icon element (should be a Vy icon using `currentColor`). */
  children: ReactNode;
}

const TileRoot = styled('div', {
  shouldForwardProp: (prop) => prop !== 'tileColor',
})<{ tileColor: VyIconTileColor }>(({ theme, tileColor }) => ({
  width: 36,
  height: 36,
  padding: 6,
  borderRadius: 8,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  backgroundColor: semantic.bg.subtle,
  color: theme.palette[tileColor].main,
}));

export const VyIconTile = forwardRef<HTMLDivElement, VyIconTileProps>(
  function VyIconTile({ color = 'neutral', children, ...rest }, ref) {
    return (
      <TileRoot ref={ref} tileColor={color} {...rest}>
        {children}
      </TileRoot>
    );
  },
);
